import React, { useState, useEffect } from 'react';

interface SettingsProps {
  onSettingsChange: (settings: {
    useAuth: boolean;
    apiEndpoint: string;
    authToken: string;
    requestBody?: any;
  }) => void;
}

const DEFAULT_API_ENDPOINT = 'https://api.pipecat.daily.co/v1/public/sandra-voice-web/start';
const NOVANT_DEMO_ENDPOINT = 'https://api.cortex.cerebrium.ai/v4/p-151ed52d/sandra-voice-agent-dev/connect';
const VOICE_AGENT_DEMO_ENDPOINT = 'https://api.cortex.cerebrium.ai/v4/p-151ed52d/voice-agent-demo/connect';
const DEFAULT_AUTH_TOKEN = '';

const PRESET_BODIES: Record<string, any> = {
  [DEFAULT_API_ENDPOINT]: {
    createDailyRoom: true,
    dailyRoomProperties: {
      enable_dialout: true
    }
  },
  [NOVANT_DEMO_ENDPOINT]: {
    createDailyRoom: true
  },
  [VOICE_AGENT_DEMO_ENDPOINT]: {
    dialoutNumber: "+18888949002",
    clientName: "novant"
  }
};

export function Settings({ onSettingsChange }: SettingsProps) {
  const [useAuth, setUseAuth] = useState(true);
  const [apiEndpoint, setApiEndpoint] = useState(DEFAULT_API_ENDPOINT);
  const [authToken, setAuthToken] = useState(DEFAULT_AUTH_TOKEN);
  const [customRequestBody, setCustomRequestBody] = useState(JSON.stringify(PRESET_BODIES[DEFAULT_API_ENDPOINT], null, 2));
  const [isCustomEndpoint, setIsCustomEndpoint] = useState(false);
  const [isBodyReadOnly, setIsBodyReadOnly] = useState(true);

  useEffect(() => {
    onSettingsChange({ 
      useAuth, 
      apiEndpoint,
      authToken: useAuth ? authToken : '',
      requestBody: getRequestBody()
    });
  }, [useAuth, apiEndpoint, authToken, customRequestBody, isCustomEndpoint, onSettingsChange]);

  const getRequestBody = () => {
    if (isCustomEndpoint && customRequestBody.trim()) {
      try {
        return JSON.parse(customRequestBody);
      } catch (error) {
        console.error('Invalid JSON in custom request body:', error);
        return {};
      }
    }
    if (PRESET_BODIES[apiEndpoint]) {
      return PRESET_BODIES[apiEndpoint];
    }
    return { createDailyRoom: true };
  };

  const handleDefaultEndpoint = () => {
    setApiEndpoint(DEFAULT_API_ENDPOINT);
    setUseAuth(true);
    setAuthToken(DEFAULT_AUTH_TOKEN);
    setIsCustomEndpoint(false);
    setCustomRequestBody(JSON.stringify(PRESET_BODIES[DEFAULT_API_ENDPOINT], null, 2));
    setIsBodyReadOnly(true);
  };

  const handleNovantDemo = () => {
    setApiEndpoint(NOVANT_DEMO_ENDPOINT);
    setUseAuth(false);
    setAuthToken('');
    setIsCustomEndpoint(false);
    setCustomRequestBody(JSON.stringify(PRESET_BODIES[NOVANT_DEMO_ENDPOINT], null, 2));
    setIsBodyReadOnly(true);
  };

  const handleVoiceAgentDemo = () => {
    setApiEndpoint(VOICE_AGENT_DEMO_ENDPOINT);
    setUseAuth(false);
    setAuthToken('');
    setIsCustomEndpoint(false);
    setCustomRequestBody(JSON.stringify(PRESET_BODIES[VOICE_AGENT_DEMO_ENDPOINT], null, 2));
    setIsBodyReadOnly(false);
  };

  const handleCustomEndpoint = () => {
    setIsCustomEndpoint(true);
    setIsBodyReadOnly(false);
    setCustomRequestBody(`{
  "example": "value"
}`);
  };

  const handleEndpointChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndpoint = e.target.value;
    setApiEndpoint(newEndpoint);
    // Check if it's a predefined endpoint
    const isPredefined = [DEFAULT_API_ENDPOINT, NOVANT_DEMO_ENDPOINT, VOICE_AGENT_DEMO_ENDPOINT].includes(newEndpoint);
    setIsCustomEndpoint(!isPredefined);
    if (isPredefined) {
      setCustomRequestBody(JSON.stringify(PRESET_BODIES[newEndpoint], null, 2));
      setIsBodyReadOnly(true);
    } else {
      setCustomRequestBody(`{
  "example": "value"
}`);
      setIsBodyReadOnly(false);
    }
  };

  const handleBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCustomRequestBody(e.target.value);
  };

  return (
    <div className="p-4 bg-white rounded-lg shadow-md mb-4">
      <div className="mb-4">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={useAuth}
            onChange={(e) => setUseAuth(e.target.checked)}
            className="form-checkbox h-5 w-5 text-blue-600"
          />
          <span className="text-gray-700">Use Authentication</span>
        </label>
      </div>
      <div className="mb-4">
        <label className="block text-gray-700 mb-2">API Endpoint</label>
        <input
          type="text"
          value={apiEndpoint}
          onChange={handleEndpointChange}
          placeholder="Enter API endpoint URL"
          className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      {useAuth && (
        <div className="mb-4">
          <label className="block text-gray-700 mb-2">Auth Token</label>
          <input
            type="text"
            value={authToken}
            onChange={(e) => setAuthToken(e.target.value)}
            placeholder="Enter auth token"
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      )}
      <div className="mb-4">
        <label className="block text-gray-700 mb-2">Custom Request Body (JSON)</label>
        <textarea
          value={customRequestBody}
          onChange={handleBodyChange}
          placeholder='{"key": "value"}'
          rows={4}
          className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
          readOnly={isBodyReadOnly}
          style={{ backgroundColor: isBodyReadOnly ? '#f3f4f6' : 'white', color: '#111827' }}
        />
        <p className="text-xs text-gray-500 mt-1">Enter valid JSON format</p>
      </div>
      <div className="flex flex-wrap space-x-2 border-t pt-4">
        <button
          onClick={handleDefaultEndpoint}
          className="flex-1 min-w-[150px] px-4 py-2 bg-blue-600 text-white border border-blue-700 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
        >
          Try Sandra Pipecat Cloud
        </button>
        <button
          onClick={handleNovantDemo}
          className="flex-1 min-w-[150px] px-4 py-2 bg-green-600 text-white border border-green-700 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
        >
          Novant Demo
        </button>
        <button
          onClick={handleVoiceAgentDemo}
          className="flex-1 min-w-[150px] px-4 py-2 bg-purple-600 text-white border border-purple-700 rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors"
        >
          Novant Voice Agent Demo 2
        </button>
        <button
          onClick={handleCustomEndpoint}
          className="flex-1 min-w-[150px] px-4 py-2 bg-yellow-600 text-black border border-yellow-700 rounded-md hover:bg-yellow-700 focus:bg-yellow-700 focus:text-black focus:outline-none focus:ring-2 focus:ring-yellow-500 transition-colors"
          style={{ backgroundColor: '#eab308' }}
        >
          Custom Request
        </button>
      </div>
    </div>
  );
} 